import React from 'react'
import { useFormContext } from 'react-hook-form'
import { Grid, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { RegisteredDecimalField, RegisteredIntegerField } from '../../../../components/fields/NumericField'
import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import { RegisteredTextField } from '../../../../components/fields/TextField'
import { FormTable, FormTableRow } from '../../issues/FormTable'
import { RegisteredDatePicker } from '../../../../components/fields/DatePicker'
import { ConvertibleEntity } from 'types/ConvertibleEntity'

function TermsFields({
    convertible,
    setIsCustomFormDirty,
}: {
    convertible: ConvertibleEntity
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const { t } = useTranslation()
    const formMethods = useFormContext()

    if (!convertible) return null

    const isVwapChecked = !!formMethods.watch('vwap')

    const validatePercentage = () => (_value: string) => {
        if (parseFloat(_value) === 0) return false

        return true
    }

    return (
        <>
            <FormTable>
                <FormTableRow label={t('pages.convertibles.maturityDate')}>
                    <Stack spacing={2}>
                        <RegisteredDatePicker name='maturityDate' label={t('pages.convertibles.maturityDate')} />
                        <RegisteredCheckBox
                            label='Kan konverteras när som helst'
                            name='convertAnytime'
                            onChange={() => setIsCustomFormDirty(true)}
                        />
                    </Stack>
                </FormTableRow>
                <FormTableRow label='Räntor och avgifter'>
                    <Stack direction='column' spacing={2}>
                        <RegisteredDecimalField
                            label={t('pages.convertibles.interest.arrangementFee')}
                            name='arrangementFee'
                            validation={{ validate: validatePercentage() }}
                        />
                        <RegisteredDecimalField
                            label={t('pages.convertibles.interest.monthly')}
                            disabled={!!formMethods.getValues('annualInterest')}
                            name='monthlyInterest'
                            validation={{ validate: validatePercentage() }}
                        />
                        <Stack
                            direction='row'
                            sx={{
                                alignContent: 'center',
                            }}
                        >
                            <RegisteredCheckBox
                                label='STIBOR'
                                name='stibor'
                                disabled={!!formMethods.getValues('monthlyInterest')}
                                onChange={() => setIsCustomFormDirty(true)}
                            />
                            <RegisteredDecimalField
                                label={t('pages.convertibles.interest.annual')}
                                disabled={!!formMethods.getValues('monthlyInterest')}
                                name='annualInterest'
                                validation={{ validate: validatePercentage() }}
                            />
                        </Stack>
                    </Stack>
                </FormTableRow>
                <FormTableRow label='Fast konvertering'>
                    <RegisteredDecimalField
                        label='Fast konverteringskurs'
                        name='fixedConversionPrice'
                        disabled={isVwapChecked}
                    />
                </FormTableRow>
                <FormTableRow label='Rörlig konvertering'>
                    <Stack spacing={2}>
                        <RegisteredCheckBox
                            label='VWAP'
                            name='vwap'
                            disabled={!!formMethods.getValues('fixedConversionPrice')}
                            onChange={() => setIsCustomFormDirty(true)}
                        />
                        {isVwapChecked ? (
                            <>
                                <RegisteredDecimalField label='Rabatt' name='vwapDiscount' disabled={!isVwapChecked} />
                                <RegisteredIntegerField label='Antal dagar' name='vwapDays' disabled={!isVwapChecked} />
                                <RegisteredDecimalField label='Minimumkurs' name='priceMin' disabled={!isVwapChecked} />
                                <RegisteredDecimalField label='Maximumkurs' name='priceMax' disabled={!isVwapChecked} />
                            </>
                        ) : null}
                    </Stack>
                </FormTableRow>

                <FormTableRow label='Särskilda villkor'>
                    <RegisteredTextField
                        label={t('pages.convertibles.noncashConsideration')}
                        name='noncashConsideration'
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </FormTableRow>

                <FormTableRow label='Kreditram'>
                    <Stack>
                        <RegisteredIntegerField
                            label='Kreditram'
                            name='creditLine'
                            validation={{
                                validate: (value: number) => value !== 0,
                            }}
                        />
                    </Stack>
                </FormTableRow>
            </FormTable>
            <Grid
                spacing={2}
                direction='row'
                container
                sx={{
                    pt: 4,
                }}
            >
                <Grid size={6}>
                    <RegisteredTextField
                        name='internalComment'
                        sx={{ width: '100%' }}
                        label={t('entities.issue.fields.internalComment')}
                        multiline
                        minRows={3}
                    />
                </Grid>
                <Grid size={6}>
                    <RegisteredTextField
                        name='externalComment'
                        sx={{ width: '100%' }}
                        label={t('entities.issue.fields.externalComment')}
                        multiline
                        minRows={3}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default TermsFields
